/**
 * MapControlsToolbar Component
 *
 * Map control toolbar with 6 buttons: Layers, Reset View, 3D Toggle, Zoom In, Zoom Out, Go to My Location
 * Uses ToolbarIconButton component for consistent styling
 *
 * Features:
 * - Desktop (no form): Horizontal layout at bottom center of map
 * - Desktop (form open): Horizontal layout at bottom, centered in available space
 * - Mobile: Horizontal layout at bottom center
 * - Integrates with Zustand for user location and layer state
 * - Dynamic positioning based on form panel width and sidebar state
 * - 100% CSS Modules
 *
 * @module components/map/MapControlsToolbar
 */

import {
  memo,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  CSSProperties,
} from 'react';
import {
  Layers,
  Focus,
  Navigation,
  Loader,
  Mountain,
  Share2,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useGeoMarkupStore } from '../../../store';
import { selectHasHiddenArtifactLayers } from '@/store/selectors';
import {
  useDeviceLayout,
  useSlidePanelWidth,
  useOffsetParentWidth,
} from '../../../hooks';
import { useZoomControls } from '../../../hooks/useZoomControls';
import { ToolbarIconButton } from '@/components/ui/ToolbarIconButton';
import { MapZoomControls } from '@/components/ui/MapZoomControls';
import {
  TOOLBOX_ICON_SIZE,
  COORD_PRECISION_THRESHOLD,
  ZOOM_PRECISION_THRESHOLD,
  TASK_FORM_PANEL_MARGIN,
  MOBILE_OVERLAY_OFFSET_ABOVE_PREVIEW,
  MOBILE_OVERLAY_OFFSET_ABOVE_MINIMIZED,
} from '../../../constants';
import { DEFAULT_CENTER, DEFAULT_ZOOM } from '../../../config/maplibre';
import type { MapControlsToolbarProps } from './MapControlsToolbar.types';
import classes from './MapControlsToolbar.module.css';

/**
 * MapControlsToolbar Component
 *
 * Vertical toolbar for map controls (layers, reset, zoom, GPS location)
 */
export const MapControlsToolbar = memo(function MapControlsToolbar({
  onResetView,
  className = '',
  mapRef,
  isMapReady,
  isAboveMinimizedPanel = false,
  isAbovePreviewPanel = false,
  onShareClick,
  isShareActive = false,
  resetCenter = DEFAULT_CENTER,
  resetZoom = DEFAULT_ZOOM,
}: MapControlsToolbarProps) {
  const { t } = useTranslation();
  const { isMobile } = useDeviceLayout();

  // Zustand: User location state
  const goToUserLocation = useGeoMarkupStore((s) => s.map.goToUserLocation);
  const isLocating = useGeoMarkupStore((s) => s.map.isLocatingUser);

  // Zustand: 3D mode state
  const is3DMode = useGeoMarkupStore((s) => s.map.is3DMode);
  const isTransitioning = useGeoMarkupStore((s) => s.map.isTransitioning);
  const toggle3DMode = useGeoMarkupStore((s) => s.map.toggle3DMode);

  // Zustand: Layer panel state
  const isLayersExpanded = useGeoMarkupStore(
    (s) => s.ui.mapControls.isLayersExpanded
  );
  const toggleLayersPanel = useGeoMarkupStore((s) => s.ui.toggleLayersPanel);
  const hasHiddenLayers = useGeoMarkupStore(selectHasHiddenArtifactLayers);

  // Zustand: Panel positioning (desktop only)
  const isAnyPanelResizing = useGeoMarkupStore((s) => s.ui.isAnyPanelResizing);

  // Slide panel width for dynamic positioning (reactive during resize)
  // useSlidePanelWidth hook handles panel priority and mobile detection
  const slidePanelWidth = useSlidePanelWidth();

  // Width of the toolbar's positioning container (its offsetParent: the map
  // container in the authenticated view, the map area in the public share view).
  // Measured directly instead of derived from window width + assumed sidebar,
  // so centering is correct in both layouts and updates when the container
  // resizes (e.g. the public list collapse/expand).
  const toolbarRef = useRef<HTMLDivElement>(null);
  // Desktop only: the toolbar is absolutely positioned, so its offsetParent is
  // the map container; on mobile it is fixed (offsetParent null) and centering
  // uses a bottom offset instead, so observation is disabled.
  const containerWidth = useOffsetParentWidth(toolbarRef, !isMobile);

  // Zoom controls (shared hook)
  const { isZoomInDisabled, isZoomOutDisabled, handleZoomIn, handleZoomOut } =
    useZoomControls({ mapRef, isMapReady });

  // Local state for reset-view disabled
  const [isResetDisabled, setIsResetDisabled] = useState(true);

  // Calculate dynamic positioning (left offset for desktop, bottom offset for mobile)
  // The desktop offset centers the toolbar within its positioning container,
  // measured via containerWidth (see toolbarRef ResizeObserver below).
  const toolbarStyle = useMemo<CSSProperties>(() => {
    if (isMobile) {
      // Mobile: Dynamic bottom position above preview panel (350px)
      if (isAbovePreviewPanel) {
        return {
          '--toolbar-bottom-offset': MOBILE_OVERLAY_OFFSET_ABOVE_PREVIEW,
        } as CSSProperties;
      }
      // Mobile: Dynamic bottom position above minimized panel (64px - only TaskDetails, form is hidden)
      if (isAboveMinimizedPanel) {
        return {
          '--toolbar-bottom-offset': MOBILE_OVERLAY_OFFSET_ABOVE_MINIMIZED,
        } as CSSProperties;
      }
      return {}; // Standard position from CSS
    }

    // Desktop with panel: center in the space left of the floating panel,
    // relative to the toolbar's own container (not the window).
    if (slidePanelWidth > 0 && containerWidth > 0) {
      const availableWidth =
        containerWidth - slidePanelWidth - TASK_FORM_PANEL_MARGIN;
      return {
        '--toolbar-left-offset': `${availableWidth / 2}px`,
      } as CSSProperties;
    }

    // Desktop without panel: Default center (50% from CSS)
    return {};
  }, [
    isMobile,
    isAbovePreviewPanel,
    isAboveMinimizedPanel,
    slidePanelWidth,
    containerWidth,
  ]);

  // Update reset-view button state when map moves
  useEffect(() => {
    const map = mapRef?.current;
    if (!map || !isMapReady) return;

    const updateResetState = () => {
      const center = map.getCenter();
      const zoom = map.getZoom();

      const isViewAtDefault =
        Math.abs(center.lng - resetCenter[0]) < COORD_PRECISION_THRESHOLD &&
        Math.abs(center.lat - resetCenter[1]) < COORD_PRECISION_THRESHOLD &&
        Math.abs(zoom - resetZoom) < ZOOM_PRECISION_THRESHOLD;

      setIsResetDisabled(isViewAtDefault);
    };

    updateResetState();
    map.on('moveend', updateResetState);

    return () => {
      map.off('moveend', updateResetState);
    };
  }, [mapRef, isMapReady, resetCenter, resetZoom]);

  const handleLayersClick = useCallback(() => {
    toggleLayersPanel();
  }, [toggleLayersPanel]);

  return (
    <div
      ref={toolbarRef}
      className={`${classes.toolbar} semi-transparent-panel ${className} ${isAnyPanelResizing ? classes.resizing : ''}`}
      style={toolbarStyle}
      data-map-controls-toolbar
    >
      <ToolbarIconButton
        icon={<Layers size={TOOLBOX_ICON_SIZE} />}
        tooltip={t('tooltips.map.toggleLayers')}
        active={isLayersExpanded}
        onClick={handleLayersClick}
        variant="transparent"
        showIndicator={hasHiddenLayers}
      />

      <ToolbarIconButton
        icon={<Focus size={TOOLBOX_ICON_SIZE} />}
        tooltip={t('tooltips.map.resetView')}
        onClick={onResetView}
        disabled={isResetDisabled}
        variant="transparent"
      />

      <ToolbarIconButton
        icon={<Mountain size={TOOLBOX_ICON_SIZE} />}
        tooltip={
          is3DMode ? t('tooltips.map.toggle2D') : t('tooltips.map.toggle3D')
        }
        onClick={toggle3DMode}
        active={is3DMode}
        disabled={isTransitioning}
        variant="transparent"
      />

      <MapZoomControls
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        isZoomInDisabled={isZoomInDisabled}
        isZoomOutDisabled={isZoomOutDisabled}
        orientation="horizontal"
      />

      <ToolbarIconButton
        icon={
          isLocating ? (
            <Loader size={TOOLBOX_ICON_SIZE} />
          ) : (
            <Navigation size={TOOLBOX_ICON_SIZE} />
          )
        }
        tooltip={
          isLocating
            ? t('tooltips.map.locatingUser')
            : t('tooltips.map.goToMyLocation')
        }
        onClick={() => goToUserLocation()}
        disabled={isLocating}
        variant="transparent"
      />

      {onShareClick && (
        <ToolbarIconButton
          icon={<Share2 size={TOOLBOX_ICON_SIZE} />}
          tooltip={t('share.managerTitle')}
          onClick={onShareClick}
          active={isShareActive}
          variant="transparent"
        />
      )}
    </div>
  );
});
